"use client";

import { createContext, useContext, useState, useEffect, ReactNode, useRef } from "react";
import { useRouter } from "next/navigation";

// Interface para o perfil completo do usuário
interface UserProfile {
  id: number;
  email: string;
  full_name?: string;
  saldo_tokens: number;
  role: string;
  cargo?: string;
  is_superuser: boolean;
  // adicione outros campos conforme seu UserResponse do backend
}

interface AuthContextType {
  token: string | null;
  user: string | null; // Mantido como string (email) para compatibilidade
  userProfile: UserProfile | null; // NOVO: Objeto completo com saldo
  setUserProfile: (profile: UserProfile | null) => void; // Para atualização manual
  refreshUserProfile: () => Promise<void>; // Para forçar recarga do servidor
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<string | null>(null); // Email apenas
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null); // Perfil completo
  const [isLoading, setIsLoading] = useState(true);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();

  // Função para buscar dados frescos do usuário
  const refreshUserProfile = async () => {
    const currentToken = token || localStorage.getItem("validus_token");
    if (!currentToken) return;

    try {
      const res = await fetch(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserProfile(data);
        // Sincroniza o email também
        if (data.email) setUser(data.email); 
      } else if (res.status === 401) {
        // Se o servidor retornar 401, o token expirou ou é inválido
        logout();
      }
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
    }
  };

  // Função para configurar o timer de logout automático
  const setupLogoutTimer = (t: string) => {
    try {
      // Limpa timer anterior se existir usando a Ref (evita atrasos de estado)
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }

      const base64Url = t.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      
      if (payload.exp) {
        const expirationTime = payload.exp * 1000; // exp é em segundos
        const currentTime = Date.now();
        const delay = expirationTime - currentTime;

        if (delay > 0) {
          logoutTimerRef.current = setTimeout(() => {
            console.warn("Sessão expirada. Deslogando automaticamente...");
            logout();
          }, delay);
        } else {
          // Já expirou
          logout();
        }
      }
    } catch (e) {
      console.error("Erro ao processar expiração do token:", e);
    }
  };

  // Controle de frequência de renovação (Throttling)
  const lastCheckTimeRef = { current: 0 };

  // Função para renovar o token silenciosamente
  const checkAndRefreshToken = async (currentToken: string) => {
    const now = Date.now();
    // Só verifica se passou pelo menos 1 minuto desde a última checagem
    if (now - lastCheckTimeRef.current < 60000) return;
    
    lastCheckTimeRef.current = now;

    try {
      const base64Url = currentToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      
      if (payload.exp) {
        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();
        const timeLeft = expirationTime - currentTime;

        // Se faltar menos de 40% do tempo total ou menos de 15 minutos, renova
        // Assumindo um token padrão de 24h, 30% é bastante tempo, 
        // mas para sessões curtas isso garante que nunca expire enquanto usa.
        const fifteenMinutes = 15 * 60 * 1000;
        
        if (timeLeft < fifteenMinutes) {
          console.log("🔄 Renovando sessão por atividade...");
          const res = await fetch(`${API_URL}/auth/refresh`, {
            method: "POST",
            headers: { Authorization: `Bearer ${currentToken}` }
          });

          if (res.ok) {
            const data = await res.json();
            setToken(data.access_token);
            localStorage.setItem("validus_token", data.access_token);
            // RESET CRÍTICO: Reinicia o timer com o novo tempo
            setupLogoutTimer(data.access_token);
          }
        }
      }
    } catch (e) {
      console.error("Erro ao verificar/renovar token:", e);
    }
  };

  // Inicialização e Monitoramento de Atividade
  useEffect(() => {
    let activityTimeout: NodeJS.Timeout;

    const resetInactivityTimer = () => {
      // Se houver atividade, tentamos renovar o token se necessário
      const currentToken = localStorage.getItem("validus_token");
      if (currentToken) {
        checkAndRefreshToken(currentToken);
      }

      // Timer de inatividade absoluta (ex: 4 horas sem NENHUMA interação)
      if (activityTimeout) clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => {
        if (localStorage.getItem("validus_token")) {
           console.warn("🚪 Deslogando por inatividade absoluta.");
           logout();
        }
      }, 4 * 60 * 60 * 1000); // 4 horas
    };

    const initAuth = async () => {
      const storedToken = localStorage.getItem("validus_token");
      const storedUser = localStorage.getItem("validus_user");

      if (storedToken) {
        setToken(storedToken);
        setUser(storedUser);
        setupLogoutTimer(storedToken);

        try {
            const res = await fetch(`${API_URL}/users/me`, {
                headers: { Authorization: `Bearer ${storedToken}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUserProfile(data);
            } else if (res.status === 401) {
                logout();
            }
        } catch (e) {
            console.error("Falha ao carregar perfil inicial:", e);
        }
      }
      setIsLoading(false);
    };

    // Ouvintes de atividade
    window.addEventListener("mousemove", resetInactivityTimer);
    window.addEventListener("keydown", resetInactivityTimer);
    window.addEventListener("click", resetInactivityTimer);

    initAuth();

    return () => {
      window.removeEventListener("mousemove", resetInactivityTimer);
      window.removeEventListener("keydown", resetInactivityTimer);
      window.removeEventListener("click", resetInactivityTimer);
      if (activityTimeout) clearTimeout(activityTimeout);
    };
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
        const formData = new URLSearchParams();
        formData.append("username", email);
        formData.append("password", password);

        const res = await fetch(`${API_URL}/token`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: formData,
        });

        if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.detail || "Login falhou.");
        }

        const data = await res.json();
        
        setToken(data.access_token);
        setUser(email);
        localStorage.setItem("validus_token", data.access_token);
        localStorage.setItem("validus_user", email);

        // Configura o timer de logout
        setupLogoutTimer(data.access_token);

        // Busca o perfil imediatamente após login para ter o saldo
        await refreshUserProfile(); 

        router.push("/dashboard");
        
    } catch (error) {
        console.error("Erro no Login:", error);
        throw error;
    } finally {
        setIsLoading(false);
    }
  };

  const loginWithGoogle = async (idToken: string) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/google/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: idToken }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const error: any = new Error(errData.detail || "Login com Google falhou.");
        error.status = res.status;
        throw error;
      }

      const data = await res.json();

      setToken(data.access_token);
      localStorage.setItem("validus_token", data.access_token);

      await refreshUserProfile();
    } catch (error: any) {
      // Não loga como erro no console se for 404 (usuário não encontrado), 
      // pois o front já trata isso redirecionando para o cadastro.
      if (error?.status !== 404) {
        console.error("Erro no Login com Google:", error);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Limpa o timer de logout usando a Ref
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    setToken(null);
    setUser(null);
    setUserProfile(null);
    localStorage.removeItem("validus_token");
    localStorage.removeItem("validus_user");
    setIsLoading(false);
    router.push("/login");
  };

  return (
    <AuthContext.Provider value={{ 
        token, 
        user, 
        userProfile, 
        setUserProfile, 
        refreshUserProfile, 
        login,
        loginWithGoogle, 
        logout, 
        isAuthenticated: !!token, 
        isLoading 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
